/**
 * Per-learner deep dive: header, comprehension fingerprint (topic radar +
 * strengths/gaps), concept-level detail, module attempts with the exact missed
 * items and misconceptions, and the per-student next best actions.
 */

import { ArrowLeft, Clock, Repeat, Eye, CheckCircle2, XCircle, AlertTriangle, Activity, BookOpenCheck } from 'lucide-react';
import type { ClassRoster, Learner } from './mockData';
import {
  attemptOf, conceptMasteryOf, conceptsOfTopic, conceptLabel, misconceptionLabel, studentActions, topicMasteryOf,
} from './mockData';
import { Panel, Donut, Radar, MasteryTag, MASTERY_META, Pill } from './charts';
import { NextBestActions } from './NextBestActions';

const SHORT_TOPIC: Record<string, string> = {
  electrostatics: 'Electrostatics', 'current-electricity': 'Current', 'ray-optics': 'Optics', 'em-waves': 'EM Waves',
};
const daysAgo = (n: number) => (n <= 0 ? 'Today' : n === 1 ? 'Yesterday' : `${n} days ago`);
const ENGAGE_META = { high: { label: 'High engagement', color: '#2F8F80' }, medium: { label: 'Medium engagement', color: '#C99A3B' }, low: { label: 'Low engagement', color: '#CB5B6B' } } as const;

export function StudentProfile({ roster, learner, onBack, onSelectTopic }: {
  roster: ClassRoster; learner: Learner; onBack?: () => void; onSelectTopic: (topicId: string) => void;
}) {
  const masteredCount = learner.conceptMastery.filter((c) => c.state === 'mastered').length;
  const attemptedCount = learner.conceptMastery.filter((c) => c.state !== 'not-attempted').length;
  const eng = ENGAGE_META[learner.engagementLevel];
  const strengths = learner.conceptMastery.filter((c) => c.state === 'mastered');
  const developing = learner.conceptMastery.filter((c) => c.state === 'developing');
  const gaps = learner.conceptMastery.filter((c) => c.state === 'emerging');
  const radarAxes = roster.topics.map((t) => ({ label: SHORT_TOPIC[t.id] ?? t.title, value: (attemptOf(learner, t.id)?.scorePercent ?? 0) / 100 }));

  return (
    <div>
      {onBack && (
        <button onClick={onBack} className="mb-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#6D28D9] transition-colors hover:text-[#7C3AED] dark:text-[#B69BF5]">
          <ArrowLeft className="h-4 w-4" /> Back to class
        </button>
      )}

      {/* header */}
      <div className="flex flex-wrap items-center gap-5 rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
        <Donut percent={learner.overallMasteryPercent} size={104}
          color={learner.overallMasteryPercent >= 70 ? '#2F8F80' : learner.overallMasteryPercent >= 55 ? '#C99A3B' : '#CB5B6B'}
          caption="mastery" />
        <div className="min-w-[200px] flex-1">
          <div className="flex items-center gap-2.5">
            <h1 className="font-serif text-[28px] font-medium tracking-tight">{learner.firstName}</h1>
            <Pill>Roll {learner.rollNo}</Pill>
          </div>
          <div className="mt-1 text-[13px] text-[#8A8593]">{roster.name} · {roster.subject}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Pill color={eng.color}><Activity className="h-3 w-3" />{eng.label}</Pill>
            <Pill><BookOpenCheck className="h-3 w-3" />{masteredCount} of {roster.concepts.length} concepts mastered</Pill>
            <Pill><Clock className="h-3 w-3" />Active {daysAgo(learner.lastActiveDaysAgo).toLowerCase()}</Pill>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-5 text-center">
          <Stat label="Modules" value={`${learner.attempts.length}/${roster.topics.length}`} />
          <Stat label="Concepts seen" value={`${attemptedCount}`} />
          <Stat label="Open gaps" value={`${gaps.length}`} tone={gaps.length ? 'warn' : 'good'} />
        </div>
      </div>

      {/* fingerprint + actions */}
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1.25fr_1fr]">
        <Panel title="Comprehension fingerprint" sub="Where this learner is strong and where the gaps are">
          <div className="flex flex-wrap items-center gap-6">
            <div className="h-[230px] w-[230px] shrink-0 text-[#6D28D9] dark:text-[#B69BF5]">
              <Radar axes={radarAxes} />
            </div>
            <div className="min-w-[180px] flex-1 space-y-4">
              <FingerColumn title="Strengths" items={strengths.map((c) => c.conceptTag)} state="mastered" empty="No mastered concepts yet" />
              <FingerColumn title="Developing" items={developing.map((c) => c.conceptTag)} state="developing" empty="Nothing in progress" />
              <FingerColumn title="Gaps & misconceptions" items={gaps.map((c) => c.conceptTag)} state="emerging" empty="No open gaps"
                misc={gaps.filter((g) => g.lastMisconceptionTag).map((g) => g.lastMisconceptionTag!)} />
            </div>
          </div>
        </Panel>

        <Panel>
          <NextBestActions scope="student" actions={studentActions(learner, roster)}
            title="What to do next" sub={`Tailored to ${learner.firstName}`} onOpenTopic={onSelectTopic} />
        </Panel>
      </div>

      {/* concept detail */}
      <div className="mt-4">
        <Panel title="Concept detail" sub="Score on every concept, grouped by topic">
          <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
            {roster.topics.map((t) => (
              <div key={t.id}>
                <div className="mb-2 flex items-center justify-between">
                  <h4 className="font-serif text-[14px] font-semibold">{t.title}</h4>
                  <MasteryTag state={topicMasteryOf(learner, t.id)} />
                </div>
                <div className="space-y-2">
                  {conceptsOfTopic(t.id).map((c) => {
                    const m = conceptMasteryOf(learner, c.tag)!;
                    const meta = MASTERY_META[m.state];
                    const pctVal = m.total ? Math.round((m.correct / m.total) * 100) : 0;
                    return (
                      <div key={c.tag}>
                        <div className="mb-1 flex items-center justify-between text-[12.5px]">
                          <span className="flex items-center gap-1.5">
                            {c.label}
                            {m.lastMisconceptionTag && <AlertTriangle className="h-3 w-3 text-[#CB5B6B]" />}
                          </span>
                          <span className="tabular-nums text-[#8A8593]">{m.total ? `${m.correct}/${m.total}` : 'not started'}</span>
                        </div>
                        <div className="h-[7px] w-full overflow-hidden rounded-full bg-black/[0.06] dark:bg-white/[0.08]">
                          <div className="h-full rounded-full" style={{ width: `${pctVal}%`, background: meta.color, opacity: m.state === 'not-attempted' ? 0.3 : 1 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* attempts */}
      <div className="mt-4">
        <Panel title="Module attempts" sub="Score, time on task, engagement and the items missed">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {roster.topics.map((t) => {
              const a = attemptOf(learner, t.id);
              if (!a) {
                return (
                  <div key={t.id} className="rounded-xl border border-dashed border-black/15 p-4 text-[12.5px] text-[#8A8593] dark:border-white/15">
                    <span className="font-serif text-[14px] font-semibold text-[#56505F] dark:text-[#B2B0C0]">{t.title}</span> — not started yet.
                  </div>
                );
              }
              const wrongByConcept = new Map<string, number>();
              const miscTags = new Set<string>();
              for (const q of a.questions) {
                if (!q.isCorrect) {
                  wrongByConcept.set(q.conceptTag, (wrongByConcept.get(q.conceptTag) ?? 0) + 1);
                  if (q.chosenMisconceptionTag) miscTags.add(q.chosenMisconceptionTag);
                }
              }
              return (
                <button key={t.id} onClick={() => onSelectTopic(t.id)}
                  className="rounded-xl border border-black/[0.07] bg-black/[0.015] p-4 text-left transition-colors hover:bg-black/[0.03] dark:border-white/[0.08] dark:bg-white/[0.02] dark:hover:bg-white/[0.05]">
                  <div className="flex items-center justify-between">
                    <h4 className="font-serif text-[14px] font-semibold">{t.title}</h4>
                    <span className="inline-flex items-center gap-1 text-[12px] font-bold tabular-nums"
                      style={{ color: a.scorePercent >= 70 ? '#1F6B5F' : a.scorePercent >= 55 ? '#9A7320' : '#B23B4E' }}>
                      {a.passed ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />} {a.scorePercent}%
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-3.5 gap-y-1 text-[11.5px] text-[#8A8593]">
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{Math.round(a.durationMs / 60000)} min</span>
                    <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />{a.watchPercent}% watched</span>
                    <span className="inline-flex items-center gap-1"><Repeat className="h-3 w-3" />{a.replays} replays</span>
                    <span className="tabular-nums">{a.correct}/{a.total} correct</span>
                  </div>
                  {miscTags.size > 0 && (
                    <div className="mt-2.5 space-y-1">
                      {Array.from(miscTags).map((m) => (
                        <div key={m} className="flex items-start gap-1.5 text-[11.5px] leading-[1.45] text-[#B23B4E]">
                          <AlertTriangle className="mt-[1px] h-3 w-3 shrink-0" />
                          <span>{misconceptionLabel(m)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'warn' | 'good' }) {
  const c = tone === 'warn' ? 'text-[#B23B4E]' : tone === 'good' ? 'text-[#1F6B5F]' : '';
  return (
    <div>
      <div className={`font-serif text-[24px] font-medium leading-none tabular-nums ${c}`}>{value}</div>
      <div className="mt-1 text-[10.5px] font-semibold uppercase tracking-wide text-[#8A8593]">{label}</div>
    </div>
  );
}

function FingerColumn({ title, items, state, empty, misc }: {
  title: string; items: string[]; state: 'mastered' | 'developing' | 'emerging'; empty: string; misc?: string[];
}) {
  const meta = MASTERY_META[state];
  // resolve concept tags to labels
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide" style={{ color: meta.text }}>
        <i className="h-2 w-2 rounded-full" style={{ background: meta.color }} /> {title}
      </div>
      {items.length === 0 ? (
        <p className="text-[12px] text-[#8A8593]">{empty}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {items.map((tag) => <ConceptChip key={tag} tag={tag} color={meta.color} />)}
        </div>
      )}
      {misc && misc.length > 0 && (
        <ul className="mt-2 space-y-1">
          {Array.from(new Set(misc)).map((m) => (
            <li key={m} className="flex items-start gap-1.5 text-[11.5px] leading-[1.45] text-[#B23B4E]">
              <AlertTriangle className="mt-[1px] h-3 w-3 shrink-0" />{misconceptionLabel(m)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ConceptChip({ tag, color }: { tag: string; color: string }) {
  return (
    <span className="rounded-md border px-2 py-0.5 text-[11.5px] font-medium"
      style={{ color, borderColor: `${color}40`, background: `${color}12` }}>
      {conceptLabel(tag)}
    </span>
  );
}
